import { axios } from '@/services/api/axiosClient';
import type { TableFilterData, UserPreference } from '@/types/Base';
import type { User } from '@/types/Users';
import type { SalesPerson, SalesPersonType } from '@/types/SalesPersons';
import { type SalesArea } from '@/types/SalesAreas';

import { validate } from '@/utils/errors/helpers';

import { getUserPreferencesSchema } from '@/types/schemas/user/getUserPreferences';
import { getUsersSchema } from '@/types/schemas/user/getUsers';
import { getSalesPersonTypesSchema } from '@/types/schemas/user/getSalesPersonTypes';
import { getSalesPersonsSchema } from '@/types/schemas/user/getSalesPersons';
import { getSalesAreasSchema } from '@/types/schemas/user/getSalesAreas';
import { getSalesPersonsAccessLevelsSchema } from '@/types/schemas/user/getSalesPersonsAccessLevels';
import { getRolesSchema } from '@/types/schemas/user/getRoles';
import type { RoleDetails } from '@/types/RoleDetails';
import { getRoleDetailsSchema } from '@/types/schemas/user/getRoleDetail';
import type { UserDetail } from '@/types/UserDetails';
import { getUserDetailsSchema } from '@/types/schemas/user/getUserDetails';
import { getSalesPersonByIdSchema } from '@/types/schemas/user/getSalesPersonById';
import type { SalesPersonDC } from '@/types/SalesPersonDC';
import { getSalesPersonsDCSchema } from '@/types/schemas/user/getSalesPersonsDC';

export async function getUserPreferences() {
  const response = await axios.get(`/v2/users/user-preferences`);
  if (response) return validate(getUserPreferencesSchema, response.data);
  return null;
}

export async function updateUserPreferences(userPreference: UserPreference) {
  const response = await axios.post(`/v2/users/user-preferences`, userPreference);
  if (response) return response.data;
  return null;
}

export async function getAllUsers(): Promise<User[]> {
  const response = await axios.get(`/v2/users/users`);
  return validate(getUsersSchema, response.data.data);
}

export async function getUserById(id: string): Promise<UserDetail | null> {
  const response = await axios.get(`/v2/users/users/${id}`, {
    validateStatus: (status: number) => status == 404 || status == 200,
  });
  if (response.status === 404 || response.data.isFailure) return null;
  const userResponse = validate(getUserDetailsSchema, response.data.value);
  return userResponse as UserDetail;
}

export async function addOrEditUser(user: UserDetail) {
  const response = await axios.post('/v2/users/users', user, {
    validateStatus: (status: number) => status === 500 || status === 400 || status === 201,
  });
  if (response.status === 201) return true;
  return false;
}

export async function removeUsersByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/users/users/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

//SalesPersons

export async function getSalesPersons(companySiteID?: string) {
  let url = '/v2/users/sales-persons';
  if (companySiteID) url += `?companySiteID=${companySiteID}`;
  const response = await axios.get(url);
  return validate(getSalesPersonsSchema, response.data.data);
}

export async function addOrEditSalesperson(salesperson: SalesPerson) {
  const response = await axios.post('/v2/users/sales-persons', salesperson, {
    validateStatus: (status: number) => status === 500 || status === 400 || status === 201,
  });
  if (response.status === 201) return '';
  return response.data.error ? response.data.error : '';
}

export async function removeSalesPersonsByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/users/sales-persons/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getSalesPersonTypes() {
  const response = await axios.get('/v2/users/salesperson-types');
  return validate(getSalesPersonTypesSchema, response.data.data);
}

export async function addOrEditSalespersonType(salespersonType: SalesPersonType) {
  const response = await axios.post('/v2/users/salesperson-types', salespersonType, {
    validateStatus: (status: number) => status === 500 || status === 201,
  });
  if (response.status === 201) return true;
  return false;
}

export async function removeSalesPersonTypesByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/users/salesperson-types/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getSalesPersonById(id: string): Promise<SalesPerson | null> {
  const response = await axios.get(`/v2/users/salespersons/${id}`, {
    validateStatus: (status: number) => status === 404 || status == 200,
  });
  if (response.status === 404) return null;
  const valid = validate(getSalesPersonByIdSchema, response.data);
  return valid;
}

//Sales Areas

export async function getSalesAreas() {
  const response = await axios.get('/v2/users/sales-areas');
  return validate(getSalesAreasSchema, response.data.data);
}

export async function addOrEditSalesArea(salesArea: SalesArea) {
  const response = await axios.post('/v2/users/sales-areas', salesArea, {
    validateStatus: (status: number) => status === 500 || status === 201,
  });
  if (response.status === 201) return true;
  return false;
}

export async function removeSalesAreasByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/users/sales-areas/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getSalesPersonAccessLevels() {
  const response = await axios.get('/v2/users/sales-persons-access-levels');
  return validate(getSalesPersonsAccessLevelsSchema, response.data.data);
}

// Roles

export async function getAllRoles() {
  const response = await axios.get('/v2/users/roles');
  return validate(getRolesSchema, response.data.data);
}

export async function removeRolesByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/users/roles/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getRoleDetailById(id: string): Promise<RoleDetails | null> {
  const response = await axios.get(`/v2/users/roles/${id}`, {
    validateStatus: (status: number) => status == 404 || status == 200,
  });
  if (response.status === 404 || response.data.isFailure) return null;
  return validate(getRoleDetailsSchema, response.data);
}

export async function addOrEditRoleDetail(roleDetail: RoleDetails) {
  const response = await axios.post('/v2/users/roles', roleDetail, {
    validateStatus: (status: number) => status === 500 || status === 400 || status === 201,
  });
  if (response.status === 404 || response.data.isFailure)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  return null;
}

// SalesPersonsDC

export async function getSalesPersonsDCWithConditions({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  distributionChannelId = undefined,
}: TableFilterData & { distributionChannelId?: string }): Promise<{
  data: SalesPersonDC[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;

  const url = `/v2/users/sales-persons-dc/search?take=${itemsPerPage}&skip=${skip}`;

  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    isActive,
    distributionChannelId,
  });

  const valid = validate(getSalesPersonsDCSchema, response.data.data);
  return { data: valid as SalesPersonDC[], count: response.data.count };
}

export async function removeSalesPersonsDCByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/users/sales-persons-dc/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}
export async function addOrEditSalesPersonsDC(salesPersonsDC: SalesPersonDC) {
  const response: any = await axios.post('v2/users/sales-persons-dc', salesPersonsDC);
  if (response.status === 201) return true;
  return false;
}
